// Aurora rendering rules shared by the MiniApp catalogue and the renderer
// (visual-identity, Interfaces dynamiques OpenUI, rules 2 and 5;
// os-aurora-migration § 4.2, OSA-09).
import { createContext, useContext } from 'react'

export type ActionBarAction = {
  label: string
  variant?: 'primary' | 'secondary'
  actionId?: string
  formId?: string
}

const filled = (value: unknown) => typeof value === 'string' && value.trim().length > 0

// An ActionBarBlock action claims the accent when it says so, or when it
// triggers an event without a variant (the historical default).
export function claimsPrimary(action: ActionBarAction): boolean {
  if (action.variant === 'primary') return true
  return action.variant === undefined && filled(action.actionId)
}

export function primaryActionKey(action: ActionBarAction): string {
  return [action.label, action.actionId ?? '', action.formId ?? ''].join('\u0000')
}

// The renderer resolves, for the whole program, which single action keeps the
// accent: the first declared one, in tree order. Without a provider (unit
// render of one component) every claimed primary keeps it.
export const PrimaryActionContext = createContext<string | null | undefined>(undefined)

export function usePrimaryActionKey(): string | null | undefined {
  return useContext(PrimaryActionContext)
}

export function rendersAsPrimary(action: ActionBarAction, primaryKey: string | null | undefined): boolean {
  if (!claimsPrimary(action)) return false
  return primaryKey === undefined || primaryKey === primaryActionKey(action)
}

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral'

// Only semantic tones exist in the MiniApp vocabulary: anything else falls
// back to the given default instead of becoming a colour.
export function toTone(value: unknown, fallback: Tone): Tone {
  return value === 'success' || value === 'warning' || value === 'error' || value === 'info' || value === 'neutral'
    ? value
    : fallback
}
